
import Vue from 'vue';
import { Vue as VueBase, Component, Watch } from 'vue-property-decorator';
import {
  CreateShipmentValidationState,
} from '@/features/shipment/helpers/createShipmentValidation';
import WhereShipment from '@/features/shipment/components/createShipment/WhereShipment.vue';
import WhatShipment from '@/features/shipment/components/createShipment/WhatShipment.vue';
import ExtrasShipment, { FormValidationType } from '@/features/shipment/components/createShipment/ExtrasShipment.vue';
import Import from '@/features/shipment/components/createShipment/Import.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { scrollToInPage, showErrorAlertDialog } from '@/features/common/helpers';
import { isReadyForShipmentPrefetch, createPrefetcher } from '@/features/common/helpers/prefetch';
import moment from 'moment';
import { getAddressesBook } from '@/features/shipment/services/addresses-book.services';
import { getUserPreferences, patchPreferences } from '@/features/settings/services/preferences.services';
import { UserPreferences } from '@/features/settings/types';
import { getCityProvinceResidential, getInProgressData } from '@/features/shipment/services/shipment.services';
import { EcommerceSettings } from '@/features/ecommerce/types';
import { getCreateShipmentNotifications } from '@/features/notifications/helpers/createShipment';
import { defaultInProgressData } from '@/features/common/raw';
import { VForm, VInput } from '@/features/common/types';
import {
  FormFieldError,
  validateAndScrollToError,
} from '@/features/common/helpers/scrollToError';
import { createShipmentFormFields } from '@/features/shipment/constants/createShipmentFormFields';
import { trackCreateFormError, trackCreateFormStart } from '@/features/common/helpers/tracked-events';
import {
  GetRatesData, InProgress, ShipFrom, ShipTo, SignatureType, LandingPageEstimateData,
} from '../types';
import {
  handleImportOrderAndContinueShipment, isOwnShippingAddress,
  getShipToAddressInPlainText, parseLandingPageEstimate,
  isSoldToDataValid,
} from '../helpers/createShipment';

@Component({
  components: {
    WhereShipment,
    WhatShipment,
    ExtrasShipment,
    Import,
  },
  provide(): { createShipmentValidationState: CreateShipmentValidationState } {
    return {
      createShipmentValidationState: (this as any).createShipmentValidationState,
    };
  },
})
export default class CreateShipment extends VueBase {
  private createShipmentValidationState = Vue.observable<CreateShipmentValidationState>({
    attempted: false,
  });

  private showImport = false;

  private isAlwaysShow = false;

  private eEcommercePrefs: EcommerceSettings | null = null;

  private validationMessage: FormValidationType = {
    isValid: true,
    message: '',
    isHint: true,
  };

  private isLoadingShipment = false;

  private formStartTracked = false;

  private prefetcher = createPrefetcher('CreateShipment');

  get source(): string {
    return this.$route.query.source as string;
  }

  get formsContainer(): HTMLElement {
    return this.$refs.formsContainer as HTMLElement;
  }

  // eslint-disable-next-line class-methods-use-this
  get shipmentSection(): HTMLElement {
    return document.querySelector('.shipment') as HTMLElement;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isCommercialInvoiceNeeded(): boolean {
    return this.$store.getters.getIsCommercialInvoice;
  }

  get whereShipmentForm(): VForm {
    return this.$store.state.shipment.whereShipmentForm as VForm;
  }

  get whatShipmentForm(): VForm {
    return this.$store.state.shipment.whatShipmentForm as VForm;
  }

  get isValidFormQuery(): boolean {
    return this.$route.query.isValid !== 'false';
  }

  @Watch('isAlwaysShow')
  private toggleShowImportPref(newValue: boolean) {
    this.syncUserPrefs('shippingSettings/showImportSection', newValue);
    this.$store.commit('patchUserPreferences', {
      key: 'shippingSettings/showImportSection',
      value: newValue,
    });
  }

  @Watch('isUserPreferences', { immediate: true })
  private replaceShowImport(newValue: number) {
    if (newValue) {
      this.showImport = this.userPreferences.shippingSettings.showImportSection;
      this.eEcommercePrefs = this.userPreferences.ecommerce;
      this.isAlwaysShow = this.userPreferences.shippingSettings.showImportSection;
    }
  }

  @Watch('validationMessage', { deep: true })
  private hideValidationMessageAfterFewSeconds(message: FormValidationType) {
    if (message.isValid) return;
    setTimeout(() => {
      this.resetValidationMessage();
    }, 7000);
  }

  @Watch('inProgressData', { deep: true })
  private triggerPrefetch() {
    const {
      fromAddress, toAddress, packages, temporaryPackage,
    } = this.inProgressData;
    // eslint-disable-next-line max-len
    const isAutoAddPackage = this.userPreferences
      ?.shippingSettings?.isAutomaticallyAddPackage ?? false;

    const { ready, packagesForPrefetch } = isReadyForShipmentPrefetch({
      fromAddress, toAddress, packages, temporaryPackage, isAutoAddPackage,
    });
    if (!ready) {
      this.prefetcher.cancel();
      return;
    }

    const data: GetRatesData = {
      currencyCode: 'CAD',
      fromAddress: {
        countryCode: fromAddress.countryCode,
        postalCode: fromAddress.postalCode,
        addr1: fromAddress.addr1 || '',
        city: fromAddress.city || '',
      },
      toAddress: {
        countryCode: toAddress.countryCode,
        postalCode: toAddress.postalCode,
        addr1: toAddress.addr1 || '',
        city: toAddress.city || '',
      },
      packages: packagesForPrefetch || packages,
      shipDateTime: moment().format('YYYY-MM-DD'),
      documentsOnly: this.inProgressData.documentsOnly,
    };

    this.prefetcher(data);
  }

  created(): void {
    eventBus.$on(eventNames.OPEN_SHIPMENT_ORDER_IMPORT, this.openImport);
    this.applyLandingPageEstimateData();
  }

  mounted(): void {
    scrollToInPage();
    this.fetchAndCommitAddressBookData();
    this.submitIfInvalidInProgress();

    const container = this.$refs.formsContainer as HTMLElement | undefined;
    if (container && container.addEventListener) {
      container.addEventListener('focusin', this.handleFormStart);
    }
  }

  private handleFormStart(): void {
    if (this.formStartTracked) return;
    this.formStartTracked = true;
    trackCreateFormStart(this.$gtag);
  }

  private async applyLandingPageEstimateData(): Promise<void> {
    if (!this.source || this.source !== 'estimate') return;

    try {
      const shipmentData = localStorage.getItem('shipmentData');
      if (!shipmentData) return;

      const parsedData: LandingPageEstimateData = JSON.parse(shipmentData);

      const estimatedData = parseLandingPageEstimate(parsedData);

      const {
        fromAddress, toAddress, extras, packages,
        isDocumentsOnly, emailNotifications, temporaryPackage,
      } = estimatedData;

      const inProgress = await getInProgressData();
      let toAddressNotChanged = true;
      let newInProgress = { ...inProgress };

      if (!inProgress) {
        newInProgress = { ...defaultInProgressData };
      } else {
        toAddressNotChanged = !inProgress || Object.values(inProgress?.toAddress as ShipFrom)
          .every((v) => v === false || v.length === 0);
      }

      const withCityProvince = async (address) => {
        if (!address?.postalCode || !address?.countryCode) return address;

        try {
          const cityProv = await getCityProvinceResidential(
            address.countryCode, address.postalCode,
          );
          if (!cityProv) return address;

          return {
            ...address,
            city: cityProv.city || address.city,
            province: cityProv.province || '',
            residential: typeof cityProv.isResidential === 'boolean' ? cityProv.isResidential : false,
          };
        } catch {
          return address;
        }
      };

      const resetValidationsRepeatedly = async () => {
        this.resetValidation();
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.resetValidation();
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.resetValidation();
      };

      const applyData = async () => {
        this.$store.commit('setLoading', true);
        const watchdog = setInterval(() => {
          this.$store.commit('setLoading', true);
        }, 50);

        try {
          if (fromAddress) {
            const userPrefRes = await getUserPreferences();
            const defaultAddress = userPrefRes?.shippingSettings?.defaultAddress;

            let addr = {
              ...fromAddress,
              company: defaultAddress?.company ?? '',
              contact: defaultAddress?.contact ?? '',
              phone: defaultAddress?.phone ?? '',
              emails: defaultAddress?.emails ?? [],
            };

            addr = await withCityProvince(addr);

            newInProgress.fromAddress = addr as ShipFrom;
          }

          if (toAddress) {
            const addr = await withCityProvince({ ...toAddress });

            newInProgress.toAddress = addr as ShipTo;
          }

          if (extras.comments) {
            newInProgress.comments = extras.comments;
          }

          if (extras.reference) {
            newInProgress.references = [extras.reference];
          }

          if (extras.signature) {
            newInProgress.signatureOptions = extras.signature as SignatureType;
          }

          newInProgress.packages = packages;

          if (temporaryPackage) {
            newInProgress.temporaryPackage = temporaryPackage;
          }

          newInProgress.documentsOnly = isDocumentsOnly;

          if (emailNotifications) {
            newInProgress.emailNotifications = [{
              email: {
                emailAddress: emailNotifications,
              },
              delivery: true,
              exception: false,
              ship: false,
            }];
          }

          this.$store.commit('setInProgress', newInProgress);
          await this.$store.dispatch('syncInProgressData');
          await resetValidationsRepeatedly();
        } finally {
          clearInterval(watchdog);
          this.$store.commit('setLoading', false);
        }
      };

      if (toAddressNotChanged) {
        await applyData();
      } else {
        eventBus.$once(eventNames.CONFIRMATION_DIALOG, applyData);

        eventBus.$once(eventNames.CONFIRMATION_DIALOG_REJECTED, async () => {
          this.resetValidation();
        });

        this.$store.commit('toggleConfirmDialog', {
          show: true,
          content: this.$t('shipment.editShipmentPrompt', {
            shipTo: getShipToAddressInPlainText(inProgress?.toAddress as ShipTo),
          }) as string,
          btnText: this.$t('buttons.ok') as string,
          title: this.$t('alertDialog.currentlyWorkingOn') as string,
        });
      }

      localStorage.removeItem('shipmentData');
    } catch { /* empty */ }
  }

  private async handleSubmit(isExtrasFormValid: boolean, extrasForm: VForm | null) {
    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();

    let isWhereFormValid = this.isWhereShipmentFormValid();
    const isWhatFormValid = this.isWhatShipmentFormValid();

    if (!isWhereFormValid) {
      eventBus.$emit(eventNames.SUBMIT_WHERE_SHIPMENT);
      // Wait for Sold to panel to render if it was auto-switched,
      // then re-validate so Vuetify picks up the new inputs
      await this.$nextTick();
      await this.$nextTick();
      if (this.whereShipmentForm) {
        this.whereShipmentForm.validate();
      }
      isWhereFormValid = this.isWhereShipmentFormValid();
    }

    if (!isWhereFormValid || !isWhatFormValid || !isExtrasFormValid) {
      this.showValidationError(extrasForm);
      return;
    }

    if (isOwnShippingAddress(this.inProgressData.fromAddress, this.inProgressData.toAddress)) {
      showErrorAlertDialog(this.$t('shipment.whereShipment.errorMessages.sameAddress') as string);
      scrollToInPage();
      return;
    }

    eventBus.$emit(eventNames.SUBMIT_WHAT_SHIPMENT);
  }

  private showValidationError(extrasForm: VForm | null) {
    const errors: FormFieldError[] = [];

    const collectErrors = (form: VForm | null | undefined) => {
      if (!form || !form.inputs) return;
      form.inputs.forEach((input: VInput) => {
        if (input.hasError) {
          errors.push({ id: input.id || '', errorMessage: input.validations?.[0] });
        }
      });
    };

    collectErrors(this.whereShipmentForm);
    collectErrors(this.whatShipmentForm);
    collectErrors(extrasForm);

    errors.forEach((err) => {
      let step = '3';
      if (this.whereShipmentForm?.inputs?.some((i: VInput) => i.id === err.id)) {
        step = '1';
      } else if (this.whatShipmentForm?.inputs?.some((i: VInput) => i.id === err.id)) {
        step = '2';
      }
      const msg = (err.errorMessage || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      trackCreateFormError(this.$gtag, {
        field: err.id,
        message: msg,
        step,
      });
    });

    const message = validateAndScrollToError(errors, createShipmentFormFields);

    this.showValidationMessage({
      isValid: false,
      message: message || '',
      isHint: true,
    });
  }

  private handleWhatShipmentSubmit(isValid: boolean) {
    if (!isValid) return;

    const isImported = handleImportOrderAndContinueShipment();
    if (isImported) return;

    this.continueShipment();
  }

  private handleSubmitImportOrder() {
    this.continueShipment();
  }

  private async continueShipment() {
    this.resetValidationMessage();

    const onNotificationsSubmit = async (): Promise<void> => {
      let nextRouteName = 'shipment-customs';

      if (!this.isCommercialInvoiceNeeded) {
        nextRouteName = 'shipment-results';
      }

      this.$router.push({ name: nextRouteName });
    };

    this.isLoadingShipment = true;
    const { packages } = this.inProgressData;
    await getCreateShipmentNotifications(packages, onNotificationsSubmit);
    this.isLoadingShipment = false;
  }

  private isWhereShipmentFormValid() {
    if (!this.whereShipmentForm) return false;

    const { fromAddress, toAddress, soldTo } = this.inProgressData;
    if (!fromAddress.addr1 || !toAddress.addr1) {
      return false;
    }

    const isFormValid = this.whereShipmentForm.validate();

    if (!isSoldToDataValid(soldTo)) {
      return false;
    }

    return isFormValid;
  }

  private isWhatShipmentFormValid() {
    const { packages } = this.inProgressData;
    const packagesLength = packages ? packages.length : 0;

    if (packagesLength) return true;

    if (!this.whatShipmentForm) return false;

    const isValid = this.whatShipmentForm.validate();
    return isValid;
  }

  private resetValidation() {
    this.createShipmentValidationState.attempted = false;

    if (this.whereShipmentForm) {
      this.whereShipmentForm.resetValidation();
    }

    if (this.whatShipmentForm) {
      this.whatShipmentForm.resetValidation();
    }
  }

  private showValidationMessage(validationMessage: FormValidationType) {
    this.validationMessage = validationMessage;
  }

  private resetValidationMessage() {
    this.validationMessage = {
      isValid: true,
      message: '',
      isHint: true,
    };
  }

  private openImport(): void {
    this.showImport = true;

    if (!this.formsContainer) return;

    this.formsContainer.classList.remove('create-shipment-fold');
    this.formsContainer.classList.add('create-shipment-foldout');
  }

  private async fetchAndCommitAddressBookData(): Promise<void> {
    const addressesBook = await getAddressesBook();
    this.$store.commit('setAddressBook', addressesBook);
  }

  private syncUserPrefs(path: string, value: unknown) {
    patchPreferences(this.userPreferences.id, [
      {
        path,
        op: 'replace',
        value,
      },
    ]);
  }

  private submitIfInvalidInProgress() {
    if (this.isValidFormQuery) return;

    eventBus.$emit(eventNames.SHIPMENT_SUBMIT);
    this.$router.push({ query: {} });
  }
}
